import { __assign, __read, __values } from "tslib";
var LOWER_MOST = Symbol('-∞');
var UPPER_MOST = Symbol('+∞');
/**
 * A group of math utilities of interval
 */
var IntervalMath = /** @class */ (function () {
    function IntervalMath(comparer) {
        var _this = this;
        /**
         *
         * @param interval an interval
         * @param value a value to test whether it belongs to the interval
         * @returns returns true only if the value belongs to the interval
         */
        this.contains = function (interval, value) {
            var _a = _this, cmp = _a.cmp, lowerOf = _a.lowerOf, upperOf = _a.upperOf;
            if (!_this.isValid(interval))
                return false;
            var lowerOpen = interval.lowerOpen, upperOpen = interval.upperOpen;
            var lowerCmp = cmp(lowerOf(interval), value);
            var upperCmp = cmp(upperOf(interval), value);
            return (lowerCmp === 0 && !lowerOpen) || (lowerCmp < 0 && upperCmp > 0) || (upperCmp === 0 && !upperOpen);
        };
        /**
         * Merge all overlapped intervals
         *
         * @param intervals an array of intervals to make a union
         * @returns the union of the intervals
         */
        this.union = function (intervals) {
            if (intervals.length <= 1)
                return intervals;
            _this.sort(intervals);
            var group = [];
            var current = intervals[0];
            for (var i = 1; i < intervals.length; i++) {
                var interval = intervals[i];
                if (_this.isOverlapped(current, interval)) {
                    current = _this.wrap(current, interval);
                }
                else {
                    group.push(current);
                    current = interval;
                }
            }
            group.push(current);
            return group;
        };
        /**
         * Get common overlap of intervals, return an empty array if there is no overlap
         *
         * @param intervals an array of intervals to make a intersection
         * @returns the intersection of all intervals
         */
        this.intersect = function (intervals) {
            var e_1, _a;
            if (intervals.length <= 1)
                return intervals;
            _this.sort(intervals);
            var current = intervals[0];
            try {
                for (var intervals_1 = __values(intervals), intervals_1_1 = intervals_1.next(); !intervals_1_1.done; intervals_1_1 = intervals_1.next()) {
                    var interval = intervals_1_1.value;
                    if (!_this.isOverlapped(current, interval)) {
                        return [];
                    }
                    current = _this.overlap(current, interval);
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (intervals_1_1 && !intervals_1_1.done && (_a = intervals_1.return)) _a.call(intervals_1);
                }
                finally { if (e_1) throw e_1.error; }
            }
            return [current];
        };
        /**
         * Return whether the interval is an non-empty interval and satisfies lower < upper
         */
        this.isValid = function (interval) {
            var _a = _this, cmp = _a.cmp, lowerOf = _a.lowerOf, upperOf = _a.upperOf;
            var res = cmp(lowerOf(interval), upperOf(interval));
            return res < 0 || (res === 0 && !interval.lowerOpen && !interval.upperOpen);
        };
        /**
         * Return the compare result of T includes Boundary
         */
        this.cmp = function (x, y) {
            if (x !== LOWER_MOST && x !== UPPER_MOST && y !== LOWER_MOST && y !== UPPER_MOST) {
                return _this.comparer.cmp(x, y);
            }
            if (x === y)
                return 0;
            return x === LOWER_MOST || y === UPPER_MOST ? -1 : 1;
        };
        /**
         * Return the lower value of interval, if the value is undefined return the lower-most boundary
         */
        this.lowerOf = function (interval) {
            return interval.lower === undefined ? LOWER_MOST : interval.lower;
        };
        /**
         * Return the upper value of interval, if the value is undefined return the upper-most boundary
         */
        this.upperOf = function (interval) {
            return interval.upper === undefined ? UPPER_MOST : interval.upper;
        };
        this.comparer = comparer;
    }
    /**
     * Return whether the two intervals is overlapped
     */
    IntervalMath.prototype.isOverlapped = function (intervalX, intervalY) {
        var _a;
        var _b = this, cmp = _b.cmp, lowerOf = _b.lowerOf, upperOf = _b.upperOf;
        var _c = __read([intervalX, intervalY], 2), x = _c[0], y = _c[1];
        // Ensure x.lower is lower or equal than y.lower
        var lowerCmp = cmp(lowerOf(x), lowerOf(y));
        if (lowerCmp > 0 || (lowerCmp === 0 && !x.lowerOpen && y.lowerOpen)) {
            _a = __read([y, x], 2), x = _a[0], y = _a[1];
        }
        var interCmp = cmp(upperOf(x), lowerOf(y));
        return interCmp > 0 || (interCmp === 0 && !x.upperOpen && !y.lowerOpen);
    };
    /**
     * Sort a list of intervals by it's lower end point
     *
     * @param intervals an array of intervals to be sorted
     * @returns the sorted intervals
     */
    IntervalMath.prototype.sort = function (intervals) {
        var _a = this, cmp = _a.cmp, lowerOf = _a.lowerOf;
        return intervals.sort(function (x, y) {
            var res = cmp(lowerOf(x), lowerOf(y));
            if (res !== 0 || x.lowerOpen === y.lowerOpen) {
                return res;
            }
            return x.lowerOpen ? -1 : 1;
        });
    };
    /**
     * Returns the minimum interval to contains both two intervals
     */
    IntervalMath.prototype.wrap = function (x, y) {
        var _a = this, cmp = _a.cmp, lowerOf = _a.lowerOf, upperOf = _a.upperOf;
        var res = __assign({}, x);
        var lowerCmp = cmp(lowerOf(x), lowerOf(y));
        var upperCmp = cmp(upperOf(x), upperOf(y));
        var useLowerY = lowerCmp > 0 || (lowerCmp === 0 && !y.lowerOpen);
        if (useLowerY) {
            res.lower = y.lower;
            res.lowerOpen = y.lowerOpen;
        }
        var useUpperY = upperCmp < 0 || (upperCmp === 0 && !y.upperOpen);
        if (useUpperY) {
            res.upper = y.upper;
            res.upperOpen = y.upperOpen;
        }
        return res;
    };
    /**
     * Returns the overlap of two intervals, the result maybe invalid
     */
    IntervalMath.prototype.overlap = function (x, y) {
        var _a = this, cmp = _a.cmp, lowerOf = _a.lowerOf, upperOf = _a.upperOf;
        var res = __assign({}, x);
        var lowerCmp = cmp(lowerOf(x), lowerOf(y));
        var upperCmp = cmp(upperOf(x), upperOf(y));
        var useLowerY = lowerCmp < 0 || (lowerCmp === 0 && !x.lowerOpen);
        if (useLowerY) {
            res.lower = y.lower;
            res.lowerOpen = y.lowerOpen;
        }
        var useUpperY = upperCmp > 0 || (upperCmp === 0 && !x.upperOpen);
        if (useUpperY) {
            res.upper = y.upper;
            res.upperOpen = y.upperOpen;
        }
        return res;
    };
    return IntervalMath;
}());
export { IntervalMath };
//# sourceMappingURL=interval.js.map